import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  AsyncPopoverItem,
  ButtonSpinner,
  DefaultButtonTheme,
  HintContainer,
  LoadingButtonTheme,
  Popover,
  RoundedButton,
} from '@sparelabs/nutshell-legacy'
import React, { useState } from 'react'
import { ErrorHandler } from 'src/helpers'

export interface IFloatingMenuOption<OptionEnum = string> {
  key: OptionEnum
  label: string
  icon?: IconProp
  iconColor?: string
  hint?: string
  disabled?: boolean
  width?: string
}

interface IFloatingMenuProps<OptionEnum = string> {
  icon: IconProp
  title: string
  onClick: (key: OptionEnum) => Promise<void> | void
  disabled?: boolean
  disabledHint?: string
  options: Array<IFloatingMenuOption<OptionEnum>>
  alignRight?: boolean
  toggleWidth?: string
  optionWidth?: string
}

export const FloatingMenu = <OptionEnum extends string>({
  icon,
  title,
  onClick,
  disabled,
  disabledHint,
  options,
  alignRight,
  optionWidth,
}: IFloatingMenuProps<OptionEnum>) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleOptionClick = async (optionKey: OptionEnum) => {
    if (loading) {
      return
    }
    setLoading(true)
    try {
      handleToggle()
      await onClick(optionKey)
    } catch (error) {
      ErrorHandler.handleError(error as Error)
    } finally {
      setLoading(false)
    }
  }

  const renderOption = (option: IFloatingMenuOption<OptionEnum>) => (
    <AsyncPopoverItem
      key={`${option.key}`}
      disabled={option.disabled}
      onClick={async () => {
        if (!option.disabled) {
          await handleOptionClick(option.key)
          setOpen(false)
        }
      }}
      icon={option.icon}
      iconColor={option.iconColor}
      description={option.hint}
      title={option.label}
      width={optionWidth ?? option.width}
    />
  )

  if (loading) {
    return (
      <RoundedButton disabled={true} buttonTheme={LoadingButtonTheme} onClick={() => ({})}>
        <ButtonSpinner />
        {title}
      </RoundedButton>
    )
  }

  if (disabled) {
    const disabledButton = (
      <RoundedButton icon={icon} disabled={true} buttonTheme={DefaultButtonTheme} onClick={() => ({})}>
        {title}
      </RoundedButton>
    )
    return disabledHint ? <HintContainer>{disabledHint}</HintContainer> : disabledButton
  }

  return (
    <Popover icon={icon} toggleTitle={title} toggle={handleToggle} isOpen={open} alignRight={alignRight}>
      {options.map((option) => renderOption({ ...option, width: optionWidth ?? option.width }))}
    </Popover>
  )
}
